import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, TextField, Typography,
} from '@mui/material'
import BatteryFullIcon from '@mui/icons-material/BatteryFull'
import Battery60Icon from '@mui/icons-material/Battery60'
import Battery30Icon from '@mui/icons-material/Battery30'
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef, type MRT_Localization,
} from 'material-react-table'
import { MRT_Localization_HE } from 'material-react-table/locales/he'
import { MRT_Localization_ES } from 'material-react-table/locales/es'
import { MRT_Localization_AR } from 'material-react-table/locales/ar'
import { DeviceLogEntry, getDeviceLogs, updateDevice } from '../../services/gateways'
import i18n from '../../translations/i18n'

export function useMrtLocalization(): MRT_Localization | undefined {
  const { i18n } = useTranslation()
  const lang = (i18n.language || 'en').split('-')[0]
  return useMemo(() => {
    if (lang === 'he') return MRT_Localization_HE
    if (lang === 'es') return MRT_Localization_ES
    if (lang === 'ar') return MRT_Localization_AR
    return undefined // en + th fall back to MRT's English defaults
  }, [lang])
}

/** Current UI language, for Intl/toLocale* date and number formatting. */
export function uiLocale(): string {
  return i18n.language || 'en'
}

/** Compact relative time ("5m", "3h"), in the current UI language. */
export function timeAgo(iso: string | null): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return i18n.t('devices.agoNow')
  if (m < 60) return i18n.t('devices.agoMinutes', { count: m })
  const h = Math.floor(m / 60)
  if (h < 24) return i18n.t('devices.agoHours', { count: h })
  return i18n.t('devices.agoDays', { count: Math.floor(h / 24) })
}

/** Date + time in the current UI language (he → 24h, no AM/PM). */
export function formatDateTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString(uiLocale()) : '—'
}

export function batteryColor(pct: number | null): string {
  if (pct == null) return '#9e9e9e'
  if (pct <= 15) return '#ef5350'
  if (pct <= 35) return '#f6b400'
  return '#43a047'
}

export const BatteryIcon: React.FC<{ pct: number | null; fontSize?: number }> = ({ pct, fontSize = 18 }) => {
  const sx = { fontSize, color: batteryColor(pct) }
  if (pct == null) return <BatteryAlertIcon sx={{ ...sx, color: '#9e9e9e' }} />
  if (pct <= 15) return <BatteryAlertIcon sx={sx} />
  if (pct <= 35) return <Battery30Icon sx={sx} />
  if (pct <= 70) return <Battery60Icon sx={sx} />
  return <BatteryFullIcon sx={sx} />
}

export const BatteryCell: React.FC<{ pct: number | null }> = ({ pct }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 110 }}>
    <BatteryIcon pct={pct} />
    <Box sx={{ flex: 1 }}>
      <LinearProgress
        variant="determinate"
        value={pct ?? 0}
        sx={{
          height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.08)',
          '& .MuiLinearProgress-bar': { bgcolor: batteryColor(pct), borderRadius: 3 },
        }}
      />
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 34, textAlign: 'right' }}>
      {pct != null ? `${pct}%` : '—'}
    </Typography>
  </Box>
)

const TREND_COLORS: Record<string, string> = {
  bad: '#ef5350',
  normal: '#f6b400',
  good: '#66bb6a',
  'very good': '#2e7d32',
}

export const TrendChip: React.FC<{ trend: string | null }> = ({ trend }) => {
  const { t } = useTranslation()
  if (!trend) return <>—</>
  const key = `devices.trend_${trend.replace(' ', '_')}`
  return (
    <Chip
      size="small"
      label={t(key, { defaultValue: trend })}
      sx={{ bgcolor: `${TREND_COLORS[trend] || '#9e9e9e'}22`, color: TREND_COLORS[trend] || '#9e9e9e', fontWeight: 700 }}
    />
  )
}

export const EditDeviceDialog: React.FC<{
  device: { macAddress: string; name: string | null } | null
  onClose: () => void
  onSaved: (name: string | null) => void
  notify: (msg: string, sev: 'success' | 'error') => void
}> = ({ device, onClose, onSaved, notify }) => {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => { setName(device?.name || '') }, [device])
  if (!device) return null
  const save = async () => {
    setSaving(true)
    try {
      const res = await updateDevice(device.macAddress, { name })
      notify(t('devices.toastSaved'), 'success')
      onSaved(res.name)
    } catch {
      notify(t('devices.toastSaveFailed'), 'error')
    } finally { setSaving(false) }
  }
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('devices.editDeviceTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {device.macAddress}
        </Typography>
        <TextField
          autoFocus fullWidth sx={{ mt: 2 }}
          label={t('devices.deviceNameLabel')}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save() }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('devices.cancel')}</Button>
        <Button variant="contained" disabled={saving} onClick={save}>{t('devices.save')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export const DeviceLogsTable: React.FC<{ macAddress: string }> = ({ macAddress }) => {
  const { t } = useTranslation()
  const localization = useMrtLocalization()
  const [logs, setLogs] = useState<DeviceLogEntry[]>([])
  const [rowCount, setRowCount] = useState(0)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getDeviceLogs(macAddress, pagination.pageIndex + 1, pagination.pageSize)
      .then(res => {
        if (cancelled) return
        setLogs(res.data)
        setRowCount(res.pagination.total)
      })
      .catch(() => { if (!cancelled) setLogs([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [macAddress, pagination])

  const columns = useMemo<MRT_ColumnDef<DeviceLogEntry>[]>(() => [
    {
      accessorKey: 'timestamp',
      header: t('devices.measuredAt'),
      Cell: ({ cell }) => formatDateTime(cell.getValue<string>()),
      size: 170,
    },
    {
      accessorKey: 'receivedAt',
      header: t('devices.received'),
      Cell: ({ cell }) => formatDateTime(cell.getValue<string>()),
      size: 170,
    },
    {
      accessorKey: 'trend',
      header: t('devices.trend'),
      Cell: ({ cell }) => <TrendChip trend={cell.getValue<string>()} />,
      size: 110,
    },
    {
      accessorKey: 'batteryPercent',
      header: t('devices.battery'),
      Cell: ({ cell }) => <BatteryCell pct={cell.getValue<number>()} />,
      size: 140,
    },
    {
      accessorKey: 'signal',
      header: t('devices.signal'),
      Cell: ({ cell }) => `${cell.getValue<number>()}/5`,
      size: 80,
    },
    {
      accessorKey: 'alert',
      header: t('devices.alertCol'),
      Cell: ({ cell }) => {
        const a = cell.getValue<DeviceLogEntry['alert']>()
        return a ? <Chip size="small" color="error" label={a.message || a.code} /> : '—'
      },
      size: 140,
    },
  ], [t])

  const table = useMaterialReactTable({
    columns,
    data: logs,
    localization,
    enableColumnActions: false,
    enableSorting: false,
    enableTopToolbar: false,
    manualPagination: true,
    rowCount,
    state: { pagination, isLoading: loading },
    onPaginationChange: setPagination,
    initialState: { density: 'compact' },
    getRowId: r => r.id,
    muiTablePaperProps: { variant: 'outlined', sx: { borderRadius: '8px' } },
  })

  return <MaterialReactTable table={table} />
}

export const DeviceLogsDialog: React.FC<{
  device: { macAddress: string; name: string | null } | null
  onClose: () => void
}> = ({ device, onClose }) => {
  const { t } = useTranslation()
  if (!device) return null
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceiptLongIcon /> {t('devices.logsTitle')} — {device.name || device.macAddress}
      </DialogTitle>
      <DialogContent dividers>
        <DeviceLogsTable macAddress={device.macAddress} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('devices.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
